/**
 * Tiny typed DOM builder.
 *
 * Every UI surface in this project is constructed through `el()` rather than
 * `innerHTML`. Even though our strings are author-controlled today, game copy is
 * exactly the kind of content that later grows an i18n layer, a server-supplied
 * item name, or a learner-entered nickname — and the day one of those reaches an
 * `innerHTML` sink is the day we ship an XSS. Building nodes explicitly makes
 * that class of bug structurally impossible, and costs nothing.
 */

type Child = Node | string | number | null | undefined | false;

export interface ElProps {
  class?: string;
  id?: string;
  text?: string | number;
  style?: string | Partial<CSSStyleDeclaration>;
  /** aria-*, data-*, role, type, etc. */
  attrs?: Record<string, string | number | boolean | undefined>;
  on?: Partial<{
    [K in keyof HTMLElementEventMap]: (ev: HTMLElementEventMap[K]) => void;
  }>;
  /** Listener options applied to every handler in `on`. */
  listenerOptions?: AddEventListenerOptions;
}

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props: ElProps = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);

  if (props.class) node.className = props.class;
  if (props.id) node.id = props.id;
  if (props.text !== undefined) node.textContent = String(props.text);

  if (props.style) {
    if (typeof props.style === 'string') node.style.cssText = props.style;
    else Object.assign(node.style, props.style);
  }

  if (props.attrs) {
    for (const [k, v] of Object.entries(props.attrs)) {
      if (v === undefined || v === false) continue;
      node.setAttribute(k, v === true ? '' : String(v));
    }
  }

  if (props.on) {
    for (const [type, handler] of Object.entries(props.on)) {
      node.addEventListener(
        type,
        handler as EventListener,
        props.listenerOptions
      );
    }
  }

  append(node, children);
  return node;
}

export function append(parent: Node, children: Child[]): void {
  for (const c of children) {
    if (c === null || c === undefined || c === false) continue;
    parent.appendChild(typeof c === 'object' ? c : document.createTextNode(String(c)));
  }
}

/** Remove all children without touching innerHTML. */
export function clear(node: Element): void {
  node.replaceChildren();
}

/** SVG counterpart, for icon glyphs that must scale crisply. */
export function svg(
  tag: string,
  attrs: Record<string, string | number> = {},
  ...children: Node[]
): SVGElement {
  const node = document.createElementNS('http://www.w3.org/2000/svg', tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, String(v));
  for (const c of children) node.appendChild(c);
  return node;
}

/**
 * Write text only when it actually changed.
 *
 * The HUD re-renders from a session snapshot on every frame. Assigning
 * `textContent` unconditionally dirties the node and forces layout even when the
 * string is identical, which is exactly the sort of per-frame paper cut that
 * turns a 60fps budget into 55. Comparing first costs one string compare.
 */
export function setText(node: Element, text: string): void {
  if (node.textContent !== text) node.textContent = text;
}

/**
 * Re-trigger a CSS animation class that may already be present.
 *
 * Removing and re-adding a class in the same task is a no-op — the browser never
 * commits the intermediate state, so the animation does not restart. Reading a
 * layout property in between forces a style flush, which does. This is the
 * canonical trick and the reason `void node.offsetWidth` looks pointless.
 */
export function restartAnimation(node: HTMLElement, cls: string): void {
  node.classList.remove(cls);
  void node.offsetWidth;
  node.classList.add(cls);
}

/**
 * True when the OS asks for reduced motion.
 *
 * CSS handles the declarative half via a media query; this is for the
 * imperative half — JS-driven tweens (counters, springs) that must snap to their
 * final value instead of animating.
 */
export function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}
